import Mark from 'mark.js'
import { RefObject, useEffect, useState } from 'react'

import { HIGHLIGHT_ACTIVE_CLASS, HIGHLIGHT_CLASS, SNIPPET_HIGHLIGHT_CLASS } from './constants'
import { debounceSearch } from './debounce-search'

type Props = {
  contentRef: RefObject<HTMLElement>
  inputValue: string
  sendHighlightEvent: (eventName: string) => void
  onHighlight?: (totalNumResult: number) => void
}

type UseSearchHighlightText = {
  totalNumResult: number
  currentNumResult: number
  clearHighlights: () => void
  nextHighlight: () => void
  prevHighlight: () => void
}

const scrollIntoViewProps: ScrollIntoViewOptions = {
  block: 'center',
  behavior: 'smooth',
}

export const useHighlightText = ({
  contentRef,
  inputValue,
  sendHighlightEvent,
  onHighlight,
}: Props): UseSearchHighlightText => {
  const [marker, setMarker] = useState<Mark>()
  const [highlightedElements, setHighlightedElements] = useState<Element[]>([])
  const [currentIndex, setCurrentIndex] = useState<number>(-1)

  const totalNumResult = highlightedElements.length
  const currentNumResult = currentIndex + 1

  const scrollToHighlightedElement = (activeIndex: number): void => {
    const element = highlightedElements[activeIndex]

    if (element) {
      element.scrollIntoView(scrollIntoViewProps)
    }
  }

  const changeCurrentIndex = (newIndex: number): void => {
    setCurrentIndex(newIndex)

    if (newIndex === -1) return

    const activeIndex = highlightedElements.findIndex((element: Element) =>
      element.classList.contains(HIGHLIGHT_ACTIVE_CLASS)
    )

    if (activeIndex === newIndex) {
      scrollToHighlightedElement(activeIndex)
      return
    }

    if (activeIndex !== -1) {
      const activeElement = highlightedElements[activeIndex]
      activeElement?.classList.remove(HIGHLIGHT_ACTIVE_CLASS)
    }

    const nextElement = highlightedElements[newIndex]

    if (nextElement) {
      nextElement.classList.add(HIGHLIGHT_ACTIVE_CLASS)
      scrollToHighlightedElement(newIndex)
    }
  }

  const nextHighlight = (): void => {
    if (totalNumResult < 1) return

    sendHighlightEvent('Doc.HighlightNavigationNextClicked')

    changeCurrentIndex(currentIndex === totalNumResult - 1 ? 0 : currentIndex + 1)
  }

  const prevHighlight = (): void => {
    if (totalNumResult < 1) return

    sendHighlightEvent('Doc.HighlightNavigationPreviousClicked')

    changeCurrentIndex(currentIndex === 0 ? totalNumResult - 1 : currentIndex - 1)
  }

  const removeHighlightNavigationMarks = (): void => {
    const highlighted = contentRef.current?.querySelectorAll(`.${HIGHLIGHT_CLASS}`)

    highlighted?.forEach((element: Element) => element.replaceWith(element.innerHTML))
  }

  const clearHighlights = (): void => {
    removeHighlightNavigationMarks()
    setHighlightedElements([])
    setCurrentIndex(-1)
  }

  const updateHighlightedElements = (): void => {
    if (!contentRef.current) return
    const elements = [
      ...Array.from(contentRef.current.querySelectorAll(`.${SNIPPET_HIGHLIGHT_CLASS}`)),
      ...Array.from(
        contentRef.current.querySelectorAll(`.${HIGHLIGHT_CLASS}:not(.${SNIPPET_HIGHLIGHT_CLASS})`)
      ),
    ]

    setHighlightedElements(elements)
    onHighlight?.(elements.length)

    sendHighlightEvent('Doc.SearchPerformed')
  }

  useEffect(() => {
    if (!contentRef?.current) return
    setMarker(new Mark(contentRef.current))
  }, [contentRef])

  useEffect(() => {
    if (!marker) return
    if (!inputValue) {
      clearHighlights()
      return
    }

    debounceSearch({
      marker,
      keyword: inputValue,
      done: updateHighlightedElements,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker, inputValue])

  useEffect(() => {
    if (!marker) return

    changeCurrentIndex(totalNumResult === 0 ? -1 : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedElements])

  return {
    totalNumResult,
    currentNumResult,
    clearHighlights,
    nextHighlight,
    prevHighlight,
  }
}
